/**
 * Tiny fetch wrapper for per-game authored stages.
 *
 *   data/stages/<gameId>/index.json   → ["stage01.json", "stage02.json"]
 *   data/stages/<gameId>/stage01.json → the actual stage payload
 *
 * Used by TrainSimScene (and any future game) to load stages designed
 * in StageEditorScene and dropped into data/stages/. Browser-only, no
 * server-side write — saving is handled in the editor by triggering
 * a download.
 *
 * The editor itself uses a similar but in-memory flow: it keeps the
 * working draft in IndexedDB (hydrated into a sync cache at boot via
 * `hydrateDrafts`) so closing/reopening the editor doesn't lose
 * progress and big stages don't hit the localStorage quota.
 */

/**
 * Build the URL for a per-game stages folder file. Paths use forward
 * slashes regardless of host OS — the dev server in server/dev.js
 * normalises them when serving from disk.
 *
 * @param {string} gameId e.g. 'trainsim'
 * @param {string} fileName e.g. 'stage01.json' or 'index.json'
 */
function buildStageUrl(gameId, fileName) {
    return `data/stages/${gameId}/${fileName}`;
}

/**
 * Returns the list of stage filenames for a given game. Falls back to an
 * empty array if the index isn't shipped yet so the caller can show an
 * empty "no stages" state instead of crashing.
 *
 * @param {string} gameId
 * @returns {Promise<string[]>}
 */
export async function listStages(gameId) {
    try {
        const res = await fetch(buildStageUrl(gameId, 'index.json'));
        if (!res.ok) return [];
        const data = await res.json();
        if (!Array.isArray(data)) return [];
        return data.filter((s) => typeof s === 'string');
    } catch (err) {
        console.warn(`[StageLoader] No index for "${gameId}":`, err.message);
        return [];
    }
}

/**
 * Fetch a single stage file and return its JSON payload.
 *
 * @param {string} gameId
 * @param {string} fileName e.g. 'stage01.json'
 * @returns {Promise<object|null>}
 */
export async function loadStage(gameId, fileName) {
    try {
        const res = await fetch(buildStageUrl(gameId, fileName));
        if (!res.ok) {
            console.warn(`[StageLoader] Stage "${gameId}/${fileName}" returned ${res.status}`);
            return null;
        }
        const data = await res.json();
        if (!data || typeof data !== 'object') return null;
        return data;
    } catch (err) {
        console.warn(`[StageLoader] Failed to load "${gameId}/${fileName}":`, err.message);
        return null;
    }
}

/**
 * Trigger a browser download of a stage JSON. Used by the editor's
 * Save button.
 *
 * @param {object} stage
 * @param {string} [filename] defaults to `<stage.id>.json`
 */
export function downloadStageJson(stage, filename) {
    const safeId = (stage?.id || 'stage').replace(/[^a-z0-9_-]/gi, '_');
    const name = filename || `${safeId}.json`;
    const blob = new Blob([JSON.stringify(stage, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

const LS_DRAFT_PREFIX = 'mini-games-stage-draft.';

// ── Editor draft persistence ───────────────────────────────────────
//
// The editor stores the in-progress stage so a refresh / close+reopen
// doesn't lose work. Distinct from the committed stages in
// `data/stages/<gameId>/` — those are static files shipped with the
// build.
//
// Storage is IndexedDB (localStorage's ~5 MB quota is too tight for
// big stages with many layers), fronted by an in-memory cache so the
// editor keeps its simple SYNCHRONOUS load/save API:
//   - `hydrateDrafts()` runs once at boot: loads every IDB draft into
//     the cache and migrates any legacy localStorage drafts over.
//   - `loadDraft` reads the cache; `saveDraft` updates the cache and
//     write-throughs to IDB in the background.

const DRAFT_DB_NAME = 'mini-games-drafts';
const DRAFT_DB_VERSION = 1;
const DRAFT_STORE = 'drafts';

const _draftCache = new Map();
let _draftsHydrated = false;

function openDraftDb() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB API not present'));
        const req = indexedDB.open(DRAFT_DB_NAME, DRAFT_DB_VERSION);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(DRAFT_STORE)) {
                req.result.createObjectStore(DRAFT_STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error('IDB open failed'));
        req.onblocked = () => reject(new Error('IDB open blocked'));
    });
}

/**
 * One-time boot hydration: pull every saved draft into the in-memory
 * cache and migrate legacy localStorage drafts into IndexedDB (freeing
 * the cramped LS quota). Safe to call on browsers without IDB — the
 * cache just starts empty and `loadDraft` falls back to localStorage.
 */
export async function hydrateDrafts() {
    try {
        const db = await openDraftDb();
        await new Promise((resolve) => {
            try {
                const store = db.transaction([DRAFT_STORE], 'readonly').objectStore(DRAFT_STORE);
                const keysReq = store.getAllKeys();
                const valsReq = store.getAll();
                valsReq.onsuccess = () => {
                    const keys = keysReq.result || [];
                    const vals = valsReq.result || [];
                    keys.forEach((k, i) => _draftCache.set(String(k), vals[i]));
                    resolve();
                };
                valsReq.onerror = () => resolve();
            } catch (_) { resolve(); }
        });

        // Migrate legacy localStorage drafts (older builds) into IDB.
        const legacy = [];
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith(LS_DRAFT_PREFIX)) legacy.push(key);
            }
        } catch (_) { /* localStorage unavailable */ }
        for (const key of legacy) {
            const gameId = key.slice(LS_DRAFT_PREFIX.length);
            try {
                const stage = JSON.parse(localStorage.getItem(key));
                if (stage && !_draftCache.has(gameId)) {
                    _draftCache.set(gameId, stage);
                    await writeDraftToIdb(gameId, stage);
                }
                localStorage.removeItem(key);
            } catch (_) { /* broken legacy draft — drop it */ }
        }
        _draftsHydrated = true;
    } catch (err) {
        console.warn('[StageLoader] Draft hydration failed (falling back to localStorage):', err?.message || err);
    }
}

function writeDraftToIdb(gameId, stage) {
    return openDraftDb().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction([DRAFT_STORE], 'readwrite');
        tx.objectStore(DRAFT_STORE).put(stage, gameId);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error || new Error('IDB write failed'));
        tx.onabort = () => reject(tx.error || new Error('IDB write aborted'));
    }));
}

export function saveDraft(gameId, stage) {
    // Snapshot, so later editor mutations can't bleed into the cache.
    const snapshot = JSON.parse(JSON.stringify(stage));
    _draftCache.set(gameId, snapshot);
    writeDraftToIdb(gameId, snapshot).catch((err) => {
        // IDB broken (private mode, quota, …) — keep the old LS path
        // as a best-effort fallback so a refresh still finds the draft.
        console.warn('[StageLoader] Draft IDB write failed, using localStorage:', err?.message || err);
        try {
            localStorage.setItem(LS_DRAFT_PREFIX + gameId, JSON.stringify(snapshot));
        } catch (err2) {
            console.warn('[StageLoader] saveDraft failed:', err2.message);
        }
    });
}

export function loadDraft(gameId) {
    const cached = _draftCache.get(gameId);
    if (cached) return JSON.parse(JSON.stringify(cached));
    if (_draftsHydrated) return null;
    // Pre-hydration (or IDB-less) fallback: legacy localStorage read.
    try {
        const raw = localStorage.getItem(LS_DRAFT_PREFIX + gameId);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (err) {
        console.warn('[StageLoader] loadDraft failed:', err.message);
        return null;
    }
}

export function clearDraft(gameId) {
    _draftCache.delete(gameId);
    openDraftDb().then((db) => {
        const tx = db.transaction([DRAFT_STORE], 'readwrite');
        tx.objectStore(DRAFT_STORE).delete(gameId);
    }).catch(() => { /* nothing stored */ });
    try {
        localStorage.removeItem(LS_DRAFT_PREFIX + gameId);
    } catch (err) {
        // ignore
    }
}
